import tus from 'tus-js-client';
import merge from 'lodash/merge';
import { EventBus } from '@/services/globalEventBus';
import { decorateBasicHeaders, baseUrl } from '../http/common';
import { handleTusResponseError } from '../globalErrorHandlers';

function getFileId({ url: uploadUrl }) {
  const parts = uploadUrl.split('/');
  return parts[parts.length - 1].toUpperCase();
}

const TUS_TERMINATION_NOT_SUPPORTED_ERROR = 'tus: failed to terminate upload, originated from'
 + ' request';

const ABSOLUTE_URL_MATCHER = /^https?:\/\//i;

function getFinalUrl(url) {
  if (ABSOLUTE_URL_MATCHER.test(url)) {
    return url;
  }
  return `${baseUrl}${url}`;
}

/**
 * Constrói um upload do TUS adicionando alguns padrões da aplicação, como, por exemplo,
 * autenticação.
 * @param {File} file Arquivo
 * @param {Object} options Opções do TUS.
 * @returns {tus.Upload}
 */
function uploadBuilder(file, options) {
  const uploadOptions = merge({
    resume: false,
    removeFingerprintOnSuccess: true,
    headers: decorateBasicHeaders(),
    metadata: {
      nomeDoArquivo: file.name,
    },
  }, options);

  return new tus.Upload(file, uploadOptions);
}

/**
 * @private
 * Inicia o upload a partir de dados do arquivo.
 * @param {Object} fileData Dados do arquivo
 */
function startFileData(fileData) {
  fileData.upload.start();
}

function getPercent(current, total) {
  return current * 100 / total;
}

/**
 * Fachada de Upload para o TUS.
 *
 * Possui alguns facilidades:
 * - Unifica e consolida os eventos de onProgress e onChunkComplete do TUS em um único evento.
 * - Suporta multiplos uploads.
 * - Controle genérico de erros.
 */
export default class UploadFacade {
  /**
   * Evento de erro.
   * @event on-error
   * @type {Error}
   */
  /**
   * Evento de progresso.
   * @event on-progress
   * @type {Object}
   * @property {File} file Arquivo.
   * @property {Number} bytesSent Bytes enviados.
   * @property {Number} percentageSent Porcentagem de envio.
   * @property {Number} acceptedBytes Bytes aceitos pelo servidor (envio bem sucedido).
   * @property {Number} percentageAccepted Porcentagem recebida/aceita no servidor.
   * @property {Number} totalBytes Total de bytes.
   */
  /**
   * Evento de upload concluído com sucesso.
   * @event on-success
   */
  /**
   * Id de arquivo recuperado.
   * @event retrieved-file-id
   * @type {Object}
   * @property {File} file Arquivo.
   * @property {String} id Id do arquivo.
   */

  /**
   * Url
   * @type {String}
   * @readonly
   */
  url

  /**
   * Iniciar uploads automaticamente.
   * @type {Boolean}
   */
  autoStart

  /**
   * Configurações do controle geral de erros.
   * @type {Object}
   */
  errorHandling

  /**
   * Constrói uma fachada de Upload para o TUS.
   * @param {Object} param Parâmetros de construção do Uploader.
   * @param {String} param.url Url. Ver doc. da propriedade.
   * @param {Object} [param.listeners] Definição de ouvintes.
   * @param {any} [param.listenersScope] Escopo dos ouvintes.
   * @param {Boolean} [param.autoStart] Auto iniciar. Ver doc. da propriedade.
   * @param {File[]} [param.files] Arquivos para upload. Opcional, arquivos podem ser adicionados
   * posteriomente.
   */
  constructor({
    url,
    listeners,
    listenersScope,
    files,
    autoStart = true,
    errorHandling = {},
  }) {
    Object.assign(this, new EventBus().createObservableProxy());

    if (listeners) {
      this.onEach(listeners, listenersScope);
    }

    this.fileUploads = [];
    this.url = getFinalUrl(url);
    this.autoStart = autoStart;
    this.errorHandling = errorHandling || {};

    if (files) {
      this.appendFile(...files);
    }
  }

  /**
   * Adiciona um arquivo a listagem de uploads.
   * Inicia automaticamente o upload caso esteja configurado o autoStart=true.
   * @param {...File} files Arquivos
   */
  appendFile(...files) {
    files.forEach((file) => {
      const fileData = {
        file,
        bytesSent: 0,
        percentageSent: 0,
        acceptedBytes: 0,
        percentageAccepted: 0,
        totalBytes: null,
        upload: uploadBuilder(file, {
          endpoint: this.url,
          onProgress: (...args) => this.handleOnProgress(fileData, 'progress', ...args),
          onChunkComplete: (...args) => this.handleOnProgress(fileData, 'chunkComplete', ...args),
          onSuccess: (...args) => this.emit('on-success', file, ...args),
          onError: (...args) => this.handleOnError(fileData, ...args),
        }),
      };
      this.fileUploads.push(fileData);

      if (this.autoStart) {
        fileData.upload.start();
      }
    });
  }

  /**
   * Inicia ou continua o upload de um arquivo em específico.
   * @param {File} file Arquivo.
   */
  start(file) {
    const fileData = this.getFileData(file);
    startFileData(fileData);
  }

  /**
   * Inicia ou continua todos os uploads.
   */
  startAll() {
    this.fileUploads.forEach(startFileData);
  }

  /**
   * Para o upload de um arquivo.
   * @param {File} file Arquivo.
   * @param {Boolean} [terminate=true] Terminar o upload e remover arquivo. Se false, apenas pausa.
   * @returns {Promise}
   */
  stop(file, terminate = true) {
    const fileData = this.getFileData(file);
    return this.stopFileData(fileData, terminate);
  }

  /**
   * Para todos os uploads.
   * @param {Boolean} [terminate=true] Terminar o upload e remover arquivos. Se false, apenas pausa.
   * @returns {Promise}
   */
  stopAll(terminate = true) {
    return Promise.all(this.fileUploads.map(fileData => this.stopFileData(fileData, terminate)));
  }

  // ==== Membros privados daqui em diante ====

  /**
   * @private
   * Para um upload a partir dos dados do arquivo.
   * Remove da lista caso seja para finalizar (terminate).
   * @param {Object} fileData Dados do arquivo.
   * @param {Boolean} terminate Terminar o upload.
   */
  stopFileData(fileData, terminate) {
    if (terminate) {
      this.fileUploads.splice(this.fileUploads.indexOf(fileData), 1);
    }

    return new Promise((resolve, reject) => {
      fileData.upload.abort(terminate, (error) => {
        if (error && !error.message.includes(TUS_TERMINATION_NOT_SUPPORTED_ERROR)) {
          handleTusResponseError(this.errorHandling, error);
          reject(error);
          return;
        }

        resolve();
      });
    });
  }

  /**
   * @private
   * Dados dos arquivos de upload.
   * @type {Object[]}
   * @property {File} fileUploads[].file Arquivo.
   * @property {Number} fileUploads[].bytesSent Bytes enviados.
   * @property {Number} fileUploads[].percentageSent Porcentagem de envio.
   * @property {Number} fileUploads[].acceptedBytes Bytes recebidos pelo servidor.
   * @property {Number} fileUploads[].percentageAccepted Porcentagem de recebimento.
   * @property {Number} fileUploads[].totalBytes Total de bytes do envio.
   * @property {tus.Upload} fileUploads[].upload Upload do TUS.
   */
  fileUploads

  /**
   * @private
   * Retorna os dados de arquivo a partir de um File.
   * @param {File} file Arquivo;
   */
  getFileData(file) {
    return this.fileUploads.find(({ file: managedFile }) => managedFile === file);
  }

  /**
   * @private
   * Controla o progresso de upload.
   * @param {Object} fileData Dados do arquivo.
   * @param {String} event Evento que disparou.
   * @param  {...any} eventArgs Argumentos do evento.
   */
  handleOnProgress(fileData, event, ...eventArgs) {
    /* eslint-disable no-param-reassign */
    if (fileData.fileId == null) {
      fileData.fileId = getFileId(fileData.upload);
      this.emit('retrieved-file-id', { file: fileData.file, id: fileData.fileId });
    }

    switch (event) {
      case 'progress':
        // eventArgs = [bytesSent, bytesTotal]
        [fileData.bytesSent, fileData.totalBytes] = eventArgs;
        fileData.percentageSent = getPercent(fileData.bytesSent, fileData.totalBytes);
        break;
      case 'chunkComplete':
        // eventArgs = [chunkSize, bytesAccepted, bytesTotal]
        [, fileData.acceptedBytes, fileData.totalBytes] = eventArgs;
        fileData.percentageAccepted = getPercent(fileData.acceptedBytes, fileData.totalBytes);
        break;
      default:
        throw new Error(`Not supported event: ${event}`);
    }
    /* eslint-enable no-param-reassign */

    this.emit('on-progress', {
      file: fileData.file,
      bytesSent: fileData.bytesSent,
      percentageSent: fileData.percentageSent,
      acceptedBytes: fileData.acceptedBytes,
      percentageAccepted: fileData.percentageAccepted,
      totalBytes: fileData.totalBytes,
    });
  }

  /**
   * @private
   * Controla erros de upload.
   * @param {Object} fileData Dados do arquivo.
   * @param  {any} errorEvent Argumentos do evento de erro.
   */
  handleOnError({ file }, errorEvent) {
    handleTusResponseError(this.errorHandling, errorEvent);

    this.emit('on-error', file, errorEvent);
  }
}
